import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, CheckCircle2, Heart } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { safeFetchJson } from "../utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  "w-full px-4 py-2.5 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300 text-sm text-stone-800 transition-colors";

// 상세 자기소개서 필드 정의 (DB snake_case 키 기준)
const SECTIONS = [
  {
    title: "기본 정보",
    fields: [
      { key: "name", label: "이름", type: "text", placeholder: "이름" },
      { key: "name_english", label: "영문 이름 (옵션)", type: "text", placeholder: "예: Min Soo" },
      { key: "country", label: "국가", type: "text", placeholder: "예: 한국, USA" },
      { key: "city", label: "도시", type: "text", placeholder: "예: 서울, LA" },
    ],
  },
  {
    title: "연락처 (관리자 전용 공개)",
    note: "관리자에게만 공개되며, 다른 회원에게는 노출되지 않습니다.",
    fields: [
      { key: "phone", label: "전화번호", type: "tel", placeholder: "010-0000-0000", required: true },
      { key: "kakao_id", label: "카카오톡 ID", type: "text", placeholder: "카카오톡 아이디", required: true },
    ],
  },
  {
    title: "학력 / 직업",
    fields: [
      {
        key: "education",
        label: "최종학력",
        type: "select",
        options: [
          { v: "", l: "학력 선택" },
          { v: "high", l: "고졸" },
          { v: "college", l: "전문대졸" },
          { v: "university", l: "대졸" },
          { v: "graduate", l: "대학원졸" },
          { v: "doctorate", l: "박사" },
        ],
      },
      { key: "school_name", label: "학교명 (옵션)", type: "text", placeholder: "학교명" },
      { key: "job", label: "직업", type: "text", placeholder: "직업" },
      { key: "company_name", label: "회사명 (옵션)", type: "text", placeholder: "회사명" },
      { key: "position", label: "직위 (옵션)", type: "text", placeholder: "직위" },
      { key: "income", label: "소득 (옵션)", type: "text", placeholder: "예: 5000만원" },
    ],
  },
  {
    title: "결혼 / 가족",
    fields: [
      { key: "marital_status", label: "싱글여부", type: "buttons", options: ["미혼", "이혼", "사별"], required: true },
      { key: "single_years", label: "경과 기간", type: "text", placeholder: "예: 3년차", required: true, showIf: (form) => ["이혼", "사별"].includes(form.marital_status) },
      {
        key: "has_children",
        label: "자녀 유무",
        type: "select",
        options: [
          { v: "", l: "선택" },
          { v: "yes", l: "있음" },
          { v: "no", l: "없음" },
        ],
      },
    ],
  },
  {
    title: "외모 / 신체",
    fields: [
      { key: "height", label: "키", type: "text", placeholder: "예: 170cm" },
      { key: "weight", label: "몸무게", type: "text", placeholder: "예: 65kg" },
      { key: "appearance_style", label: "외모스타일", type: "text", placeholder: "예: 댄디, 캐주얼" },
      {
        key: "blood_type",
        label: "혈액형",
        type: "select",
        options: [
          { v: "", l: "혈액형 선택" },
          { v: "A", l: "A형" },
          { v: "B", l: "B형" },
          { v: "O", l: "O형" },
          { v: "AB", l: "AB형" },
        ],
      },
      { key: "mbti", label: "MBTI", type: "text", placeholder: "예: ENFP", upper: true, maxLength: 4 },
      { key: "charm_point", label: "매력 포인트", type: "text", placeholder: "나만의 매력 포인트" },
    ],
  },
  {
    title: "성향 / 생활",
    fields: [
      { key: "religion", label: "종교", type: "text", placeholder: "예: 기독교, 불교, 무교" },
      {
        key: "drinking",
        label: "음주",
        type: "select",
        options: [
          { v: "", l: "음주" },
          { v: "no", l: "안함" },
          { v: "sometimes", l: "가끔" },
          { v: "often", l: "자주" },
        ],
      },
      { key: "drinking_amount", label: "주량", type: "text", placeholder: "예: 소주 1병" },
      {
        key: "smoking",
        label: "흡연",
        type: "select",
        options: [
          { v: "", l: "흡연" },
          { v: "no", l: "비흡연" },
          { v: "sometimes", l: "가끔" },
          { v: "yes", l: "흡연" },
        ],
      },
    ],
  },
  {
    title: "취미 / 관심사",
    fields: [
      { key: "hobbies", label: "취미", type: "text", placeholder: "예: 영화감상, 등산, 요리" },
      { key: "leisure_activities", label: "특별 여가/운동/자기개발", type: "text", placeholder: "예: 주 3회 헬스, 주말 등산" },
      { key: "favorite_food", label: "좋아하는 음식", type: "text", placeholder: "예: 한식, 일식, 스테이크" },
      { key: "stress_relief", label: "나만의 스트레스 해소법", type: "text", placeholder: "예: 운동, 드라이브, 명상" },
    ],
  },
  {
    title: "가치관 / 인생관",
    fields: [
      { key: "life_motto", label: "인생좌우명", type: "text", placeholder: "인생좌우명" },
      { key: "favorite_movie", label: "인생영화", type: "text", placeholder: "인생영화" },
      { key: "favorite_book", label: "인생 책", type: "text", placeholder: "인생 책" },
      { key: "strengths_weaknesses", label: "나의 장점과 단점", type: "textarea", placeholder: "자신의 장점과 단점을 솔직하게 작성해주세요." },
      { key: "happy_moments", label: "소소하게 행복을 느끼는 순간은?", type: "textarea", placeholder: "행복을 느끼는 순간을 자유롭게 작성해주세요." },
      { key: "current_interests", label: "현재 가장 큰 관심사는?", type: "textarea", placeholder: "현재 가장 관심있는 분야나 활동을 작성해주세요." },
      { key: "proud_achievement", label: "가장 보람되게 성취해본 경험은?", type: "textarea", placeholder: "가장 보람찬 성취 경험을 작성해주세요." },
      { key: "important_values", label: "내 삶에서 가장 중요한 가치 3가지는?", type: "textarea", placeholder: "예: 가족, 건강, 자기성장" },
      { key: "future_vision", label: "미리 그려보는 10년 뒤 나의 모습은?", type: "textarea", placeholder: "10년 후 자신의 모습을 상상해서 작성해주세요." },
    ],
  },
  {
    title: "이상형 / 결혼관",
    fields: [
      { key: "ideal_type", label: "원하는 이상형", type: "textarea", placeholder: "원하는 이상형에 대해 자유롭게 작성해주세요." },
      {
        key: "dating_purpose",
        label: "이성을 찾는 목적",
        type: "select",
        options: [
          { v: "", l: "선택" },
          { v: "marriage", l: "궁극적 결혼/재혼" },
          { v: "serious", l: "진지한 만남/연인찾기" },
          { v: "casual", l: "가벼운 만남/친구찾기" },
          { v: "companion", l: "외로움 해소/호기심" },
        ],
      },
    ],
  },
];

const ALL_KEYS = SECTIONS.flatMap((s) => s.fields.map((f) => f.key)).concat(["introduction"]);

export default function SelfIntroPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isWelcome = params.get("welcome") === "1";
  const { token, isAuthenticated, loading: authLoading, refreshUser } = useAuth();

  const [form, setForm] = useState(() =>
    ALL_KEYS.reduce((acc, k) => ({ ...acc, [k]: "" }), {})
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);

  const DRAFT_KEY = "selfintro_draft";

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate("/", { replace: true });
      return;
    }
    (async () => {
      try {
        const authToken = token || localStorage.getItem("liztv_token");
        const res = await fetch(`${API_URL}/api/auth/me/full`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (res.ok) {
          const u = await res.json();
          setForm((prev) => {
            const next = { ...prev };
            ALL_KEYS.forEach((k) => {
              if (u[k] !== undefined && u[k] !== null) next[k] = u[k];
            });
            // dating_purpose 가 비어있으면 marriage_goal 로 보완
            if (!next.dating_purpose && u.marriage_goal) next.dating_purpose = u.marriage_goal;
            return next;
          });
        }
      } catch {
        /* ignore prefill errors */
      } finally {
        // 세션 끊김 등으로 저장 못하고 임시 보관된 작성 내용이 있으면 복원
        try {
          const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
          if (draft && typeof draft === "object") {
            setForm((prev) => ({ ...prev, ...draft }));
          }
        } catch { /* ignore */ }
        setLoading(false);
      }
    })();
  }, [authLoading, isAuthenticated, token, navigate]);

  const handleSave = async () => {
    if (!form.phone?.trim() || !form.kakao_id?.trim()) {
      toast.error("전화번호와 카카오톡 ID를 모두 입력해주세요.");
      return;
    }
    if (!form.marital_status) {
      toast.error("싱글여부를 선택해주세요.");
      return;
    }
    if (["이혼", "사별"].includes(form.marital_status) && !form.single_years?.trim()) {
      toast.error("이혼/사별 경과 기간을 입력해주세요. (예: 3년차)");
      return;
    }
    setSaving(true);
    try {
      // 모바일 웹뷰에서 메모리 토큰이 드롭될 수 있어 localStorage 폴백 사용
      const authToken = token || localStorage.getItem("liztv_token");
      if (!authToken) {
        // 세션이 끊긴 경우: 작성 내용을 임시 보관하고, 그 자리에서 로그인 모달을 띄운 뒤 로그인하면 자동 저장
        try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch { /* ignore */ }
        setPendingSave(true);
        setSaving(false);
        toast.info("작성하신 내용은 안전하게 저장해뒀어요 😊 이어서 진행하려면, 잠시 후 뜨는 화면에서 다시 한 번 로그인만 해주세요.", { duration: 7000 });
        window.dispatchEvent(new CustomEvent("openAuthModal", { detail: { mode: "login" } }));
        return;
      }
      // 빈 문자열 필드는 제외하고 입력된 값만 전송 (기존 값 보존)
      const payload = {};
      ALL_KEYS.forEach((k) => {
        const v = form[k];
        if (typeof v === "string" ? v.trim() !== "" : v != null) {
          payload[k] = typeof v === "string" ? v.trim() : v;
        }
      });
      if (payload.dating_purpose) payload.marriage_goal = payload.dating_purpose;

      const { ok, status, data } = await safeFetchJson(`${API_URL}/api/auth/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(payload),
      });
      if (!ok) {
        if (status === 401) {
          // 토큰 만료/무효: 임시 보관 후 재로그인 유도 → 로그인 시 자동 저장
          try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch { /* ignore */ }
          setPendingSave(true);
          setSaving(false);
          toast.info("작성하신 내용은 안전하게 저장해뒀어요 😊 이어서 진행하려면, 잠시 후 뜨는 화면에서 다시 한 번 로그인만 해주세요.", { duration: 7000 });
          window.dispatchEvent(new CustomEvent("openAuthModal", { detail: { mode: "login" } }));
          return;
        }
        throw new Error(data.detail || "저장에 실패했습니다.");
      }
      // 성공: 임시 보관 삭제
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      if (refreshUser) await refreshUser();
      if (isWelcome) {
        // 결제 직후 온보딩 흐름: 서류 인증 단계로 계속 진행
        toast.success("자기소개서가 완성되었습니다. 마지막으로 서류를 인증해주세요.");
        navigate("/profile/document?welcome=1", { replace: true });
      } else {
        // 일반 진입(무료회원 포함): 저장 후 내 프로필로 복귀
        toast.success("자기소개서가 저장되었습니다.");
        navigate("/profile", { replace: true });
      }
    } catch (e) {
      toast.error(e.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  // 재로그인 성공(토큰 확보) 시, 임시 보관한 작성 내용을 자동으로 저장
  useEffect(() => {
    const authToken = token || localStorage.getItem("liztv_token");
    if (pendingSave && authToken) {
      setPendingSave(false);
      handleSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pendingSave]);

  const renderField = (f) => {
    const labelEl = (
      <span className="block text-sm font-medium text-stone-700 mb-1.5">
        {f.label}
        {f.required && <span className="text-rose-500"> *</span>}
      </span>
    );
    if (f.type === "select") {
      return (
        <label key={f.key} className="block">
          {labelEl}
          <select
            value={form[f.key] || ""}
            onChange={(e) => set(f.key, e.target.value)}
            className={inputCls}
            data-testid={`field-${f.key}`}
          >
            {f.options.map((o) => (
              <option key={o.v} value={o.v}>
                {o.l}
              </option>
            ))}
          </select>
        </label>
      );
    }
    if (f.type === "buttons") {
      return (
        <div key={f.key} className="block">
          {labelEl}
          <div className="flex gap-3">
            {f.options.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => set(f.key, opt)}
                className={`flex-1 py-2.5 rounded-xl border text-sm transition-colors ${
                  form[f.key] === opt
                    ? "bg-burgundy-50 border-burgundy-300 text-burgundy-600 font-semibold"
                    : "bg-stone-50 border-stone-200 text-stone-500 hover:border-stone-300"
                }`}
                data-testid={`field-${f.key}-${opt}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      );
    }
    if (f.type === "textarea") {
      return (
        <label key={f.key} className="block">
          {labelEl}
          <textarea
            value={form[f.key] || ""}
            onChange={(e) => set(f.key, e.target.value)}
            rows={3}
            placeholder={f.placeholder}
            className={`${inputCls} resize-none`}
            data-testid={`field-${f.key}`}
          />
        </label>
      );
    }
    return (
      <label key={f.key} className="block">
        {labelEl}
        <input
          type={f.type}
          value={form[f.key] || ""}
          onChange={(e) => set(f.key, f.upper ? e.target.value.toUpperCase() : e.target.value)}
          maxLength={f.maxLength}
          placeholder={f.placeholder}
          className={inputCls}
          data-testid={`field-${f.key}`}
        />
      </label>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-7 h-7 animate-spin text-burgundy-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white py-12 px-4" data-testid="self-intro-page">
      <div className="max-w-2xl mx-auto">
        {isWelcome && (
          <div className="flex items-center gap-2 mb-4 text-emerald-600" data-testid="payment-welcome-banner">
            <CheckCircle2 className="w-5 h-5" />
            <span className="text-sm font-semibold">결제가 완료되었습니다. 정회원 가입을 환영합니다!</span>
          </div>
        )}

        <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-8">
          <div className="flex items-center gap-2 mb-1">
            <Heart className="w-6 h-6 text-burgundy-500" />
            <h1 className="text-2xl font-bold text-stone-900">자기소개서 완성하기</h1>
          </div>
          <p className="text-sm text-stone-500 mb-7 leading-relaxed">
            가입 시 입력하신 기초 정보에 이어, 상세 정보를 작성해 자기소개서를 완성해주세요.<br />
            정성껏 작성하실수록 더 좋은 인연을 만날 가능성이 높아집니다.
          </p>

          {/* 자기소개 (한줄 소개) */}
          <label className="block mb-6">
            <span className="block text-sm font-medium text-stone-700 mb-1.5">한 줄 자기소개</span>
            <textarea
              value={form.introduction || ""}
              onChange={(e) => set("introduction", e.target.value)}
              rows={3}
              placeholder="나를 표현하는 한 마디, 취미, 만나고 싶은 사람 등 자유롭게 작성해주세요."
              className={`${inputCls} resize-none`}
              data-testid="field-introduction"
            />
          </label>

          {SECTIONS.map((section) => (
            <div key={section.title} className="border-t border-stone-200 pt-6 mt-6">
              <h4 className="text-sm font-semibold text-burgundy-600 mb-1">{section.title}</h4>
              {section.note && <p className="text-xs text-stone-400 mb-4">{section.note}</p>}
              <div className="grid sm:grid-cols-2 gap-4 mt-3">
                {section.fields.map((f) => {
                  if (f.showIf && !f.showIf(form)) return null;
                  return (
                    <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                      {renderField(f)}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full mt-8 flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-white font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="self-intro-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>자기소개서 완성하고 시작하기 <ArrowRight className="w-4 h-4" /></>}
          </button>

          <button
            onClick={() => navigate("/community", { replace: true })}
            className="w-full mt-3 text-center text-xs text-stone-400 hover:text-stone-600 transition-colors"
            data-testid="self-intro-skip-btn"
          >
            나중에 작성하기
          </button>
        </div>
      </div>
    </div>
  );
}
